import { motion } from "framer-motion";
import { TrendingUp, MapPin } from "lucide-react";

const CASES = [
  {
    tag: "D2C · Beauty",
    city: "Mumbai",
    headline: "280% ROAS lift in 90 days for a homegrown clean-beauty brand",
    body:
      "Restructured Meta + Google accounts, refreshed creative cadence and shipped a faster Shopify storefront. CAC fell 38% while AOV climbed.",
    metrics: [
      { k: "ROAS", v: "+280%" },
      { k: "CAC", v: "-38%" },
      { k: "AOV", v: "+22%" },
    ],
    image:
      "https://images.pexels.com/photos/7814941/pexels-photo-7814941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    tag: "B2B · SaaS",
    city: "Bengaluru",
    headline: "5× qualified pipeline for a Bengaluru workflow-automation SaaS",
    body:
      "Built an ABM engine across LinkedIn + outbound + content syndication. Sales-accepted leads grew 5× and average deal cycle dropped 31%.",
    metrics: [
      { k: "MQL→SAL", v: "+412%" },
      { k: "Cycle", v: "-31%" },
      { k: "Pipeline", v: "5×" },
    ],
    image:
      "https://images.pexels.com/photos/33948766/pexels-photo-33948766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    tag: "B2C · QSR",
    city: "Pune",
    headline: "150% repeat-order rate jump for a Pune-based QSR chain",
    body:
      "Designed a WhatsApp + email lifecycle loop with geo-targeted Meta ads. Repeat purchase intervals shrank, week-2 retention nearly doubled.",
    metrics: [
      { k: "Repeat", v: "+150%" },
      { k: "Wk-2 Retn", v: "+92%" },
      { k: "Ad CPM", v: "-24%" },
    ],
    image:
      "https://images.unsplash.com/photo-1565299507177-b0ac66763828?auto=format&fit=crop&w=1200&q=70",
  },
  {
    tag: "D2C · Wellness",
    city: "Delhi NCR",
    headline: "Amazon SmartBiz launch hits 4-figure daily orders in 60 days",
    body:
      "From listings and A+ to SmartBiz catalog + sponsored ads. Search-share grew steadily, organic share-of-voice doubled in the category.",
    metrics: [
      { k: "Orders/day", v: "+1000+" },
      { k: "SoV", v: "+98%" },
      { k: "TACoS", v: "-19%" },
    ],
    image:
      "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=1200&q=70",
  },
];

export default function CaseStudies() {
  return (
    <section
      id="work"
      data-testid="case-studies-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-yellow-400 font-mono-ui text-xs tracking-[0.22em] uppercase mb-4">
              · Selected work
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-2xl">
              Real lifts.
              <br />
              <span className="brand-gradient-text">No vanity metrics.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base sm:text-lg leading-relaxed">
            Indian-market campaigns where we’re named on the NDA but the numbers
            speak loudest. Brand names withheld; results are not.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {CASES.map((c, idx) => (
            <motion.article
              key={c.headline}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.07 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/40 hover:border-yellow-400/40 transition-all"
              data-testid={`case-card-${idx}`}
            >
              <div className="relative h-56 sm:h-60 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.tag}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-950/70 backdrop-blur border border-white/10 text-xs font-mono-ui text-yellow-300">
                  {c.tag}
                </div>
                <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/70 backdrop-blur border border-white/10 text-xs text-zinc-200">
                  <MapPin size={12} /> {c.city}
                </div>
              </div>
              <div className="p-7 sm:p-8">
                <h3 className="font-display text-xl sm:text-2xl font-semibold leading-snug tracking-tight">
                  {c.headline}
                </h3>
                <p className="mt-3 text-zinc-400 leading-relaxed text-sm sm:text-base">
                  {c.body}
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {c.metrics.map((m) => (
                    <div
                      key={m.k}
                      className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                    >
                      <div className="text-xs text-zinc-500 font-mono-ui uppercase tracking-wider">
                        {m.k}
                      </div>
                      <div className="mt-1 text-lg sm:text-xl font-display font-semibold brand-gradient-text">
                        {m.v}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-500 font-mono-ui">
                  <TrendingUp size={14} className="text-yellow-400" />
                  Brand kept anonymous · NDA
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
